import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  ListItem,
  Tooltip,
  Avatar,
  Typography,
  Grid,
  SvgIcon,
  IconButton,
  ButtonBase,
  Menu,
  MenuItem,
  Grow
} from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import WarningIcon from "@material-ui/icons/Warning";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import {
  setExploreShapesIsVisible,
  setExplorePinsAreVisible,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  setConfirmationModalState,
  createExploreViewObject,
  deleteExploreViewObject,
  setRadius,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
  setExploreLastCreatedObjectId,
  highlightExploreViewObject,
  handleExploreViewObjectVisibilityChange
} from 'store/actions';
import { Constants } from 'utilities/constants';
import { viewObjects } from 'store/explore/views';
import PropertyDataObject from 'components/explore/views/objects/PropertyDataObject';
import ShapesDataObject from 'components/explore/views/objects/ShapesDataObject';
import { sendClientLog } from 'services/logService';
import { createGUID } from 'utilities';
import { extendBoundsToObject } from 'components/explore/gmap/helpers';
import { MapRefContext } from 'components/explore/Explore';
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';

const ViewObjectCard = ({
  classes,
  object,
  setObjectCardMenuIsOpen,
  handleObjectClick,
  focusedObjectId,
  highlightedObjectId,
  propertyDataDisplay,
  propertyDataIsErrored,
  shapesDataIsErrored,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  setConfirmationModalState,
  createExploreViewObject,
  deleteExploreViewObject,
  setRadius,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
  lastCreatedObjectId,
  setExploreLastCreatedObjectId,
  handleExploreViewObjectVisibilityChange,
  index,
  objectCount,
  highlightExploreViewObject
}) => {
  const mapRef = useContext(MapRefContext);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const isFocused = object.id === focusedObjectId;
  const isHighlighted = object.id === highlightedObjectId;
  const cardRef = useRef(null);
  useEffect(() => { // Effect to control mount animations- none for existing objects. Newly added objects will scroll into view, then display a mount transition
    if (!cardRef.current || object.id !== lastCreatedObjectId) return;
    let scrollTimeout;
    const checkForEnd = function(e) { // Debounced check for end of scroll event
      clearTimeout(scrollTimeout); // Reset the timer each time a scroll event fires
      scrollTimeout = setTimeout(function() {
          setExploreLastCreatedObjectId(null); // Once scrolling is done, call action to start the mount animation
          document.getElementById('viewContent').removeEventListener('scroll', checkForEnd);
      }, 100);
    }
    document.getElementById('viewContent').addEventListener('scroll', checkForEnd);
    cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    scrollTimeout = setTimeout(function() { // If added object is in view and no scroll occurs, this clears the listener and displays the object
      document.getElementById('viewContent').removeEventListener('scroll', checkForEnd);
      setExploreLastCreatedObjectId(null);
    }, 100);
  }, [lastCreatedObjectId]);

  useEffect(() => {
    if (isFocused || isHighlighted) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isFocused, isHighlighted]);

  const openMoreMenu = (e) => {
    setObjectCardMenuIsOpen(true);
    setMoreMenuAnchorEl(e.currentTarget);
  }

  const closeMoreMenu = () => {
    setObjectCardMenuIsOpen(false);
    setMoreMenuAnchorEl(null);
  }

  const handleHighlightObject = (shouldLog) => {
    highlightExploreViewObject(object.id, object.type);
    if (shouldLog) {
      sendClientLog('Info',`Explore Views - User clicked Highlight from ViewObjectCard for object id ${object.id}, layer id ${object.layerId}`);
    }
  }

  function checkActualValuesForMeasurements(object) {
    if (object.type === 'Circle') {
      if (object.data) {
        setRadius(object.data.radius)
      } else {
        setRadius(0);
      }
    } else if (object.type === 'DistanceMeasuring') {
      if (object.data) {
        setDistanceMeasuringTotalDistance(object.data.totalData ? object.data.totalData.totalDistance : '0 mi');
        setDistanceMeasuringTotalArea(object.data.totalData ? object.data.totalData.totalArea : '0 mi²');
      } else {
        setDistanceMeasuringTotalDistance('0 mi');
        setDistanceMeasuringTotalArea('0 mi²');
      }
    }
  }

  const handleObjectDetailClick = () => {
    if (!isObjectHighlightDisabled) {
      handleHighlightObject(); // Flash items on entering detail
    }
    handleObjectClick(object.id);
    checkActualValuesForMeasurements(object);
  }

  const handleVisibilityClick = () => {
    handleExploreViewObjectVisibilityChange([object.id], !object.visible);
    sendClientLog('Info',`Explore Views - User toggle visibility to ${!object.visible} for object id ${object.id}, layer id ${object.layerId}`);
  }

  const isSelectedSource = object.id === propertyDataDisplay.selectedSourceId;
  const handleDataSourceChange = e => {
    updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, selectedSourceId: object.id } });
    if (object.visible && object.isFilterSet) {
      fetchExplorePropertyData({ objectId: object.id, shouldFetch: true });
    }
    closeMoreMenu();
    sendClientLog('Info',`Explore Views - User changed property data source to object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleBestFitObject = () => {
    closeMoreMenu();
    const bounds = new window.google.maps.LatLngBounds();
    const expandedBounds = extendBoundsToObject(bounds, object, mapRef);
    if (mapRef?.current) {
      const zoom = mapRef.current.getZoom();
      fitBoundsWithZoom(mapRef.current, expandedBounds);
      if (object.type === Constants.ViewsObjectType.LABEL) { // Don't use new zoom for labels
        mapRef.current.setZoom(zoom); // Set zoom to previous value
      }
    }
    sendClientLog('Info',`Explore Views - User used best fit from ViewObjectCard for object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleFocusObject = () => {
    closeMoreMenu();
    handleExploreViewObjectVisibilityChange([object.id], true, true);
    sendClientLog('Info',`Explore Views - User used focus from ViewObjectCard for object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleFitAndFocusObject = () => {
    closeMoreMenu();
    if (object.type !== Constants.ViewsObjectType.PROPERTYDATA && object.type !== Constants.ViewsObjectType.MARKETS) {
      handleBestFitObject();
    }
    handleFocusObject();
  }

  const dupWouldExceedLimit = useMemo(() => { 
    const limit = Constants.ViewsObjectCountLimit[object.type]; 
    return objectCount + 1 > limit; 
  }, [objectCount, object.type]);

  const handleDuplicateObject = () => {
    closeMoreMenu();
    const { id, type, index, description, layerId, clientId, ...rest } = object;
    createExploreViewObject('tempObject-' + createGUID(), layerId, type, { description: 'Copy of ' + description, ...rest });
    sendClientLog('Info',`Explore Views - User duplicated object with type ${object.type} from ViewObjectCard menu for object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleDeleteObject = () => {
    closeMoreMenu();
    if (object.needsPositionSet) {
      deleteExploreViewObject(object.layerId, object.id);
      sendClientLog('Info',`Explore Views - User deleted object from ViewObjectCard menu for object id ${object.id}, layer id ${object.layerId}`);
    } else {
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: () => {
          closeConfirmationModal();
          deleteExploreViewObject(object.layerId, object.id);
          sendClientLog('Info',`Explore Views - User clicked and confirmed Delete on ViewObjectCard menu for object id ${object.id}, layer id ${object.layerId}`);
        },
        handleCancellation: () => {
          closeConfirmationModal();
        },
        titleText: 'Confirm',
        contentText: `Are you sure you want to delete object "${object.description}"?`
      });
    }
  }
  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const getObject = useMemo((type) => { // For object types that need to call actions on mount/update, mount a component here to handle it. The component can also return elements to be displayed in its card.
    switch (object.type) {
      case Constants.ViewsObjectType.PROPERTYDATA:
        return <PropertyDataObject object={object} />
      case Constants.ViewsObjectType.MARKETS:
        return <ShapesDataObject object={object} />
      default:
        return null;
    }
  }, [object]);

  const isObjectHighlightDisabled = !object.visible || (object.type === Constants.ViewsObjectType.PROPERTYDATA && !object.isFilterSet) || object.needsPositionSet;

  return (
    <Draggable
      key={`object-${object.id}`}
      draggableId={`object-${object.id}`}
      index={index}
      isDragDisabled={moreMenuAnchorEl != null}
    >
      {
        (provided) => (
          <Grow
            in={object.id !== lastCreatedObjectId}
            appear={false}
            timeout={700}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            <div
              ref={cardRef}
              className={classes.listItemContainer}
            >
              <div className={classNames(classes.cardContainer, !object.visible && classes.hiddenCard)}>
                <Tooltip title={isObjectHighlightDisabled ? '' : 'Highlight on map'}>
                  <span>
                    <ButtonBase onClick={(e) => handleHighlightObject(true)} disabled={isObjectHighlightDisabled}>
                      <Avatar className={classNames(classes.queueAvatar, isHighlighted ? classes.avatarBackgroundHighlighted : isFocused ? classes.avatarBackgroundSelected : classes.avatarBackground)}>
                        {viewObjects[object.type]?.icon
                          ? <SvgIcon>
                            <path d={viewObjects[object.type].icon} />
                          </SvgIcon>
                          : <HomeIcon/>
                        }
                      </Avatar>
                    </ButtonBase>
                  </span>
                </Tooltip>
                <ListItem
                  button
                  className={classes.listItem}
                  selected={isHighlighted}
                  classes={{ selected: classes.listItemSelected }}
                  onClick={handleObjectDetailClick}
                  disabled={!object.visible}
                >
                  <div className={classes.listItemContent}>
                    <Grid container wrap='nowrap' spacing={0} alignItems='center' direction='row' justifyContent='space-between'>
                      <Grid item className={classes.objectNameContainer}>
                        {/* <Typography className={classes.textPrimary} component="div">{viewObjects[object.type]?.displayType ?? object.type}</Typography> */}
                        <Typography className={classes.textPrimary} component="div">{object.description ?? 'Description'}</Typography>
                        {isSelectedSource &&
                          <SvgIcon fontSize='small' style={{ marginTop: -2, marginRight: 4 }}>
                            <path d="M4,3H20A2,2 0 0,1 22,5V20A2,2 0 0,1 20,22H4A2,2 0 0,1 2,20V5A2,2 0 0,1 4,3M4,7V10H8V7H4M10,7V10H14V7H10M20,10V7H16V10H20M4,12V15H8V12H4M4,20H8V17H4V20M10,12V15H14V12H10M10,20H14V17H10V20M20,20V17H16V20H20M20,12H16V15H20V12Z" />
                          </SvgIcon>
                        }
                        {(propertyDataIsErrored || shapesDataIsErrored) &&
                          <Tooltip title='Encountered an error while loading data'>
                            <WarningIcon className={classes.warning} fontSize='small' style={{ marginTop: -2 }} />
                          </Tooltip>
                        }
                      </Grid>
                    </Grid>
                    {object.needsPositionSet &&
                      <>
                        <div style={{ flexGrow: 1 }}></div>
                        <div style={{ marginBottom: -2 }}>
                          <Typography className={classes.textSecondary} component="div"><i>{'Needs position set'}</i></Typography>
                        </div>
                      </>
                    }
                    {getObject}
                  </div>
                </ListItem>
                <IconButton
                  className={classes.detailButton}
                  onClick={openMoreMenu}
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  anchorEl={moreMenuAnchorEl}
                  open={Boolean(moreMenuAnchorEl)}
                  onClose={closeMoreMenu}
                  anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom'
                  }}
                >
                  {object.type === Constants.ViewsObjectType.PROPERTYDATA &&
                    <MenuItem onClick={handleDataSourceChange} disabled={isSelectedSource}>Set as source in grid/charts</MenuItem>
                  }
                  {object.type !== Constants.ViewsObjectType.PROPERTYDATA && object.type !== Constants.ViewsObjectType.MARKETS &&
                    <MenuItem onClick={handleBestFitObject} disabled={!object.visible || object.needsPositionSet}>Fit Object on map</MenuItem>
                  }
                  {object.type !== Constants.ViewsObjectType.PROPERTYDATA && object.type !== Constants.ViewsObjectType.MARKETS &&
                    <MenuItem onClick={handleFitAndFocusObject} disabled={object.needsPositionSet}>Fit and focus Object</MenuItem>
                  }
                  <MenuItem onClick={handleFocusObject}>Focus Object</MenuItem>
                  <Tooltip title={dupWouldExceedLimit ? 'Quantity limit reached' : ''}>
                    <span>
                      <MenuItem onClick={handleDuplicateObject} disabled={dupWouldExceedLimit}>Duplicate Object</MenuItem>
                    </span>
                  </Tooltip>
                  <MenuItem onClick={handleDeleteObject}>Delete Object</MenuItem>
                </Menu>
              </div>
              <Tooltip title='Toggle object visibility'>
                <IconButton
                  className={classes.visibilityButton}
                  onClick={handleVisibilityClick}
                >
                  {object.visible
                    ? <VisibilityIcon />
                    : <VisibilityOffIcon />
                  }
                </IconButton>
              </Tooltip>
            </div>
          </Grow>
        )
      }
    </Draggable>
  );
}
const styles = theme => ({
  listItemContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 8px',
    scrollMargin: '50px'
  },
  cardContainer: {
    display: 'flex',
    borderRadius: 4,
    borderBottom: '1px solid #e4e4e4',
    overflow: 'hidden',
    position: 'relative',
    width: '100%'
  },
  hiddenCard: {
    opacity: 0.5
  },
  textPrimary: {
    fontSize: '.85rem',
    fontWeight: '500',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: 8
  },
  inlinePadding: {
    margin: '0px 5px 0px 0px',
  },
  textSecondary: {
    fontSize: '.75rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  listItem: {
    padding: 0,
    backgroundColor: '#fff',
    minWidth: 0
  },
  listItemContent: {
    padding: '3px 8px',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  listItemSelected: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)!important'
    // backgroundColor: '#ffc660!important'
  },
  queueAvatar: {
    height: '72px',
    width: '24px',
    borderRadius: 0,
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: '#fff700'
    }
  },
  avatarBackground: {
    backgroundColor: theme.palette.primary.main
  },
  avatarBackgroundSelected: {
    backgroundColor: '#ffa300'
  },
  avatarBackgroundHighlighted: {
    backgroundColor: '#fff700'
  },
  objectNameContainer: {
    overflow: 'hidden',
    display: 'flex',
    // marginBottom: 2
  },
  badgeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '4px'
  },
  visibilityButton: {
    padding: 2,
    margin: '0 -4px 0 4px'
  },
  detailButton: {
    padding: 4,
    position: 'absolute',
    right: 0,
    top: 20
  },
  warning: {
    color: theme.palette.warning.main
  }
});

const mapStateToProps = ({ exploreViews, exploreData, exploreShapesData }, ownProps) => {
  const pdo = exploreData.propertyDataObjects[ownProps.object.id];
  const sdo = exploreShapesData.shapesDataObjects[ownProps.object.id];
  return {
    propertyDataDisplay: exploreViews.selectedView?.propertyDataDisplay,
    focusedObjectId: exploreViews.focusedObjectId,
    highlightedObjectId: exploreViews.highlightedObjectId,
    lastCreatedObjectId: exploreViews.lastCreatedObjectId,
    propertyDataIsErrored: pdo?.isErrored,
    shapesDataIsErrored: sdo?.isErrored
  }
};
const mapDispatchToProps = {
  setExploreShapesIsVisible,
  setExplorePinsAreVisible,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  setConfirmationModalState,
  createExploreViewObject,
  deleteExploreViewObject,
  setRadius,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
  setExploreLastCreatedObjectId,
  highlightExploreViewObject,
  handleExploreViewObjectVisibilityChange
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ViewObjectCard);
